import React, { useState, memo } from 'react';
import { CTASection } from '../../components/CTASection';
import { Flame, CheckCircle, Zap, Award, Sparkles, Clock, Shield } from 'lucide-react';
export function HeatTransfer() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1518" />,
    title: 'Versatile Designs',
    description: 'From bold graphics to intricate details, heat transfer allows for stunning designs that stand out'
  }, {
    icon: <Clock className="w-6 h-6" data-id="element-1519" />,
    title: 'Quick Turnaround',
    description: 'Need your designs fast? Our efficient process means you can get your custom gear in no time'
  }, {
    icon: <Shield className="w-6 h-6" data-id="element-1520" />,
    title: 'Durable Quality',
    description: 'Our heat transfer prints are not only visually appealing but also built to last, ensuring your designs stay vibrant wash after wash'
  }];
  const applications = [{
    icon: '👕',
    title: 'Team Uniforms',
    description: 'Boost morale and create a sense of unity with custom team apparel'
  }, {
    icon: '🎁',
    title: 'Personalized Gifts',
    description: 'Make special occasions even more memorable with custom designs'
  }, {
    icon: '🎉',
    title: 'Event Apparel',
    description: 'Create lasting memories with custom event shirts and merchandise'
  }, {
    icon: '🏢',
    title: 'Corporate Branding',
    description: 'Professional branded apparel for your business or organization'
  }];
  const faqs = [{
    question: 'What is heat transfer printing?',
    answer: 'Heat transfer printing is a process where designs are printed onto special transfer paper or vinyl, then applied to fabric using heat and pressure. This creates vibrant, durable designs on a variety of materials.'
  }, {
    question: 'What types of fabrics work with heat transfer?',
    answer: 'Heat transfer works on a wide variety of fabrics including cotton, polyester, blends, and many synthetic materials. We can advise on the best transfer method for your specific fabric type.'
  }, {
    question: 'How long do heat transfer designs last?',
    answer: 'With proper care, heat transfer designs are extremely durable and can last for years. Our high-quality transfers are designed to withstand repeated washing and wear while maintaining vibrant colors.'
  }, {
    question: 'Can you do full-color designs with heat transfer?',
    answer: 'Absolutely! Heat transfer is perfect for full-color designs, photographs, gradients, and intricate details that would be difficult or impossible with other methods like screen printing.'
  }, {
    question: "What's the turnaround time for heat transfer orders?",
    answer: 'Our efficient heat transfer process allows for quick turnaround times. Simple orders can often be completed in a few days, while larger or more complex projects may take 1-2 weeks. Contact us for specific timeline estimates.'
  }, {
    question: 'Is there a minimum order quantity?',
    answer: 'No! Heat transfer is perfect for both small and large orders. Whether you need one custom piece or hundreds of team uniforms, we can accommodate your needs.'
  }, {
    question: 'How do I care for heat transfer apparel?',
    answer: 'Turn garments inside out before washing, use cold or warm water (not hot), and avoid harsh detergents or bleach. Tumble dry on low or hang dry. Following these simple steps will keep your designs looking great for years.'
  }];
  return <div className="flex flex-col w-full" data-id="element-1521">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1522">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1523">
          <div className="flex justify-center mb-6" data-id="element-1524">
            <Flame className="w-16 h-16 text-white" data-id="element-1525" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1526">
            Heat Transfer
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1527">
            Your designs, transformed with heat! Vibrant, durable custom apparel
            for teams, events, and special occasions. Get ready to heat things
            up! 🔥
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1528">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1529">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1530">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1531">
              Get Ready to Heat Things Up! 🔥
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1532">
              <p data-id="element-1533">
                At Wear Haus Designs, we take your creative visions and turn up
                the heat—literally! Our heat transfer services are perfect for
                bringing vibrant designs to life on a variety of fabrics.
                Whether you're looking to add a splash of color to your team
                uniforms or create custom apparel for an event, we've got you
                covered!
              </p>
            </div>
          </div>

          {/* Why Choose Heat Transfer */}
          <div className="mb-16" data-id="element-1534">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1535">
              Why Choose Heat Transfer?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1536">
              {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1537">
                  <div className="flex flex-col items-center text-center" data-id="element-1538">
                    <div className="w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white mb-4" data-id="element-1539">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1540">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1541">{benefit.description}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Applications */}
          <div className="mb-16" data-id="element-1542">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1543">
              Endless Possibilities
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto" data-id="element-1544">
              With heat transfer, the options are endless! Here are just a few
              applications:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1545">
              {applications.map((application, index) => <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1546">
                  <div className="text-4xl mb-4" data-id="element-1547">{application.icon}</div>
                  <h3 className="text-2xl font-bold text-wh-teal mb-3" data-id="element-1548">
                    {application.title}
                  </h3>
                  <p className="text-gray-700" data-id="element-1549">{application.description}</p>
                </div>)}
            </div>
          </div>

          {/* Process Highlight */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1550">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1551">
              How Heat Transfer Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1552">
              <div className="text-center" data-id="element-1553">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1554">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1555">Design</h3>
                <p className="text-white/90" data-id="element-1556">
                  Your custom design is printed onto special transfer material
                </p>
              </div>
              <div className="text-center" data-id="element-1557">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1558">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1559">Heat & Press</h3>
                <p className="text-white/90" data-id="element-1560">
                  The transfer is applied to your garment using heat and
                  pressure
                </p>
              </div>
              <div className="text-center" data-id="element-1561">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1562">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1563">Enjoy!</h3>
                <p className="text-white/90" data-id="element-1564">
                  Your vibrant, durable custom apparel is ready to wear
                </p>
              </div>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white mb-16" data-id="element-1565">
            <Flame className="w-16 h-16 mx-auto mb-6" data-id="element-1566" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1567">
              Ready to Transform Your Designs?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1568">
              Let's turn up the heat on your next project! From team uniforms to
              personalized gifts, we bring your creative visions to life with
              vibrant, lasting quality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1569">
              <a href="/Contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1570">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1571">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-id="element-1572">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1573">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1574">
            Heat Transfer FAQs
          </h2>

          <div className="space-y-4" data-id="element-1575">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1576">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-1577">
                  <h3 className="text-lg font-bold text-gray-900 pr-4" data-id="element-1578">
                    {faq.question}
                  </h3>
                  <CheckCircle className={`w-6 h-6 text-wh-pink flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-1579" />
                </button>
                {openFaq === index && <div className="px-8 pb-6" data-id="element-1580">
                    <p className="text-gray-700 leading-relaxed" data-id="element-1581">
                      {faq.answer}
                    </p>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1582" />
    </div>;
}